import React, { useState, useEffect, useRef } from "react";
import { Global } from "@emotion/react";
import {
  Box,
  Input,
  Button,
  VStack,
  FormControl,
  Heading,
  useToast,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  useDisclosure,
  Text,
  Badge,
  Spinner,
  Avatar,
  HStack,
  InputGroup,
  InputLeftElement,
  Select,
  FormLabel,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { useJsApiLoader } from "@react-google-maps/api";
import { doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../../configs/firebase";
import { useAuth } from "../../../auth/AuthContext";
import { useAppDispatch, useAppSelector } from "../../../store/hooks/hooks";
import {
  fetchHikeCars,
  addHikeCar,
  updateHikeCar,
  deleteHikeCar,
} from "../../../store/travelSlice/hikeCarsSlice";
import {
  addTrip,
  updateTrip,
  deleteTrip,
  fetchAllTrips,
  generateRecurringTrips,
  HikeTrip,
  TripRecurrenceFrequency,
} from "../../../store/travelSlice/hikeTripsSlice";
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { Search } from "lucide-react";

const cardsBorderRadii ="8px";

type LocationCoords = {
  lat: number;
  lng: number;
};

const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];

const timeOptions = Array.from({ length: 48 }, (_, i) => {
  const hours24 = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? "00" : "30";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const ampm = hours24 < 12 ? "AM" : "PM";

  return {
    value: `${hours24.toString().padStart(2, "0")}:${minutes}`,
    label: `${hours12}:${minutes} ${ampm}`,
  };
});

const getLocationAddress = (value: any) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value;
  }

  return value.address || value.text || value.label || value.name || "";
};

const getLocationLat = (value: any) => {
  if (!value || typeof value === "string") return "";

  return value.lat ?? value.latitude ?? "";
};

const getLocationLng = (value: any) => {
  if (!value || typeof value === "string") return "";

  return value.lng ?? value.longitude ?? "";
};

const buildCoords = (latValue: any, lngValue: any): LocationCoords | null => {
  const lat = Number(latValue);
  const lng = Number(lngValue);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  if (lat === 0 && lng === 0) {
    return null;
  }

  return { lat, lng };
};

const toLocalDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toInputDateValue = (value: any) => {
  if (!value) return "";

  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[0];
  }

  if (typeof value === "string") {
    return value;
  }

  if (value instanceof Date && !isNaN(value.getTime())) {
    return toLocalDateValue(value);
  }

  return "";
};

const normalizeTripRecurrenceFrequency = (
  value: any,
): TripRecurrenceFrequency => {
  const frequency = String(value || "none").toLowerCase();

  if (
    frequency === "daily" ||
    frequency === "weekly" ||
    frequency === "monthly"
  ) {
    return frequency;
  }

  return "none";
};

const normalizeRole = (value: any) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getRecordUserId = (record: any) =>
  String(
    record?.userId ||
      record?.ownerUserId ||
      // Backward compatibility for cars saved before userId existed.
      record?.driverId ||
      "",
  ).trim();

const getRecordId = (record: any) =>
  String(record?._id || record?.id || "").trim();

const extractSavedHikeCar = (response: any) => {
  const candidates = [
    response?.car,
    response?.data?.car,
    response?.data,
    response?.result?.car,
    response?.result,
    response,
  ];

  const savedCar = candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      getRecordId(candidate),
  );

  if (!savedCar) return null;

  return {
    ...savedCar,
    _id: getRecordId(savedCar),
  };
};

type DriverOption = {
  id: string;
  name: string;
  email?: string;
  photoURL?: string;
  role?: string;
  source?: "owner" | "member" | "directory";
};

const getDriverId = (value: any) =>
  String(
    value?.id || value?.uid || value?.firebaseUid || value?.userId || "",
  ).trim();

const toDriverOption = (
  value: any,
  source: DriverOption["source"],
): DriverOption | null => {
  const id = getDriverId(value);

  if (!id) return null;

  const name = String(
    value?.displayName ||
      value?.name ||
      value?.businessName ||
      value?.email ||
      id,
  ).trim();

  return {
    id,
    name: name || id,
    email: String(value?.email || "").trim() || undefined,
    photoURL:
      String(value?.photoURL || value?.avatar || "").trim() || undefined,
    role: String(value?.role || "").trim() || undefined,
    source,
  };
};

interface UserMember {
  id: string;
  name: string;
  email: string;
  role?: string;
  photoURL?: string;
  [key: string]: any;
}

interface UserSearchDropdownProps {
  onSelect: (user: UserMember) => void | Promise<void>;
}

const getBaseApiUrl = () => {
  const isBrowser = typeof window !== "undefined";
  const isLocalhost =
    isBrowser &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const localBaseUrl = process.env.REACT_APP_BACKEND_URL;
  const publicBaseUrl =
    process.env.REACT_APP_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;

  const normalizeBaseUrl = (value: string) => {
    const cleanBaseUrl = value.replace(/\/+$/, "");
    return cleanBaseUrl.endsWith("/api/v1")
      ? cleanBaseUrl
      : `${cleanBaseUrl}/api/v1`;
  };

  if (isLocalhost) {
    return localBaseUrl?.trim()
      ? normalizeBaseUrl(localBaseUrl.trim())
      : "http://localhost:5000/api/v1";
  }

  if (publicBaseUrl?.trim()) {
    return normalizeBaseUrl(publicBaseUrl.trim());
  }

  if (
    localBaseUrl?.trim() &&
    !localBaseUrl.includes("localhost") &&
    !localBaseUrl.includes("127.0.0.1")
  ) {
    return normalizeBaseUrl(localBaseUrl.trim());
  }

  return "https://api.pickdengu.mw/api/v1";
};

const API_BASE_URL = getBaseApiUrl();

const UserSearchDropdown = ({ onSelect }: UserSearchDropdownProps) => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      setSearching(false);
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setSearching(true);

      try {
        const currentUser = getAuth().currentUser;

        if (!currentUser) {
          throw new Error("You must be signed in to search users.");
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/user-directory?search=${encodeURIComponent(
            trimmedQuery,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              "Users could not be searched.",
          );
        }

        const users = Array.isArray(payload?.users)
          ? payload.users
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const normalizedUsers: UserMember[] = users
          .map((candidate: Record<string, unknown>) => {
            const id = String(candidate.uid || candidate.id || "");
            const name = String(
              candidate.displayName || candidate.name || candidate.email || id,
            );

            return {
              ...candidate,
              id,
              name,
              email: String(candidate.email || ""),
              photoURL: candidate.photoURL
                ? String(candidate.photoURL)
                : undefined,
              role: candidate.role ? String(candidate.role) : "guest",
            } as UserMember;
          })
          .filter((candidate: UserMember) => Boolean(candidate.id));

        setResults(normalizedUsers);
        setIsOpen(true);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        console.error("User search failed:", error);
        setResults([]);
        setIsOpen(false);

        toast({
          title: "User search failed",
          description:
            error instanceof Error ? error.message : "Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, toast]);

  const handleSelect = async (selectedUser: UserMember) => {
    await onSelect(selectedUser);
    setQuery("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <Box ref={containerRef} position="relative" width="100%" zIndex={80}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          {searching ? <Spinner size="sm" /> : <Search size={16} />}
        </InputLeftElement>

        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder="Search by name, email, or user ID"
          bg={theme.inputBg}
          color={theme.text}
          borderColor={theme.inputBorder}
          _placeholder={{ color: theme.inputPlaceholder }}
          _hover={{ borderColor: theme.borderStrong }}
          _focus={{
            borderColor: theme.inputBorderFocus,
            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
          }}
        />
      </InputGroup>

      {isOpen && (
        <VStack
          mt={2}
          position="relative"
          zIndex={2}
          spacing={0}
          align="stretch"
          maxH="320px"
          overflowY="auto"
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
        >
          {results.length === 0 ? (
            <Text p={4} color={theme.textMuted}>
              No matching users found.
            </Text>
          ) : (
            results.map((result) => (
              <HStack
                key={result.id}
                p={3}
                cursor="pointer"
                _hover={{ bg: theme.hover }}
                onClick={() => handleSelect(result)}
                borderBottom="1px solid"
                borderColor={theme.border}
                _last={{ borderBottom: "none" }}
              >
                <Avatar size="sm" src={result.photoURL} name={result.name} />

                <Box minW={0}>
                  <Text fontWeight="700" color={theme.text} noOfLines={1}>
                    {result.name}
                  </Text>

                  <Text fontSize="sm" color={theme.textMuted} noOfLines={1}>
                    {result.email || result.id}
                  </Text>
                </Box>
              </HStack>
            ))
          )}
        </VStack>
      )}
    </Box>
  );
};

type StablePlaceAutocompleteProps = {
  placeholder: string;
  initialValue?: string;
  resetKey: number;
  inputStyles: any;
  onAddressChange: (value: string) => void;
  onLocationSelected: (coords: LocationCoords | null) => void;
  onInvalidPlace?: () => void;
};

const StablePlaceAutocomplete: React.FC<StablePlaceAutocompleteProps> = ({
  placeholder,
  initialValue = "",
  resetKey,
  inputStyles,
  onAddressChange,
  onLocationSelected,
  onInvalidPlace,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const autocompleteRef = React.useRef<google.maps.places.Autocomplete | null>(
    null,
  );

  const onAddressChangeRef = React.useRef(onAddressChange);
  const onLocationSelectedRef = React.useRef(onLocationSelected);
  const onInvalidPlaceRef = React.useRef(onInvalidPlace);

  const [inputValue, setInputValue] = React.useState(initialValue || "");

  React.useEffect(() => {
    onAddressChangeRef.current = onAddressChange;
    onLocationSelectedRef.current = onLocationSelected;
    onInvalidPlaceRef.current = onInvalidPlace;
  }, [onAddressChange, onLocationSelected, onInvalidPlace]);

  React.useEffect(() => {
    setInputValue(initialValue || "");
  }, [resetKey, initialValue]);

  React.useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        fields: [
          "formatted_address",
          "geometry",
          "name",
          "place_id",
          "address_components",
        ],
        componentRestrictions: { country: "mw" },
        types: ["geocode"],
      },
    );

    const listener = autocompleteRef.current.addListener(
      "place_changed",
      () => {
        const place = autocompleteRef.current?.getPlace();

        const isMalawiPlace = Boolean(
          place?.address_components?.some(
            (component) =>
              component.types.includes("country") &&
              component.short_name?.toUpperCase() === "MW",
          ),
        );

        if (!isMalawiPlace) {
          setInputValue("");
          onAddressChangeRef.current("");
          onLocationSelectedRef.current(null);
          onInvalidPlaceRef.current?.();
          return;
        }

        const nextAddress =
          place?.formatted_address ||
          place?.name ||
          inputRef.current?.value ||
          "";

        const location = place?.geometry?.location;
        const nextCoords = location
          ? {
              lat: location.lat(),
              lng: location.lng(),
            }
          : null;

        setInputValue(nextAddress);
        onAddressChangeRef.current(nextAddress);
        onLocationSelectedRef.current(nextCoords);
      },
    );

    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, []);

  return (
    <Input
      ref={inputRef}
      {...inputStyles}
      placeholder={placeholder}
      value={inputValue}
      onChange={(event) => {
        const nextValue = event.target.value;

        setInputValue(nextValue);
        onAddressChangeRef.current(nextValue);

        // Manual typing means the address is not yet confirmed as a Malawi Google Maps place.
        onLocationSelectedRef.current(null);
      }}
    />
  );
};

export const HikeCarsManager = () => {
  const { user } = useAuth();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { theme } = useCustomTheme();

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const {
    isOpen: isTripModalOpen,
    onOpen: openTripModal,
    onClose: closeTripModal,
  } = useDisclosure();

  const {
    isOpen: isCarModalOpen,
    onOpen: openCarModal,
    onClose: closeCarModal,
  } = useDisclosure();

  const [members, setMembers] = useState<any[]>([]);
  const [accountOwnerDriver, setAccountOwnerDriver] =
    useState<DriverOption | null>(null);
  const [directoryDriversById, setDirectoryDriversById] = useState<
    Record<string, DriverOption>
  >({});
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [accessLoading, setAccessLoading] = useState(true);
  const [driverNamesById, setDriverNamesById] = useState<
    Record<string, string>
  >({});
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const cars = useAppSelector((state) => state.hikeCars.list);
  const trips = useAppSelector((state) => state?.hikeTrips?.list || []);
  const [locallySavedCarsById, setLocallySavedCarsById] = useState<
    Record<string, any>
  >({});
  const scheduleGenerationLoading = useAppSelector(
    (state) => state?.hikeTrips?.scheduleGenerationLoading,
  );

  const currentUserUid = String(user?.uid || "").trim();
  const isSuperAdmin = normalizeRole(currentUserRole) === "superadmin";

  const allCars = React.useMemo(() => {
    const carsById = new Map<string, any>();

    Object.values(locallySavedCarsById).forEach((car: any) => {
      const carId = getRecordId(car);

      if (carId) {
        carsById.set(carId, car);
      }
    });

    (Array.isArray(cars) ? cars : []).forEach((car: any) => {
      const carId = getRecordId(car);

      if (carId) {
        carsById.set(carId, car);
      }
    });

    return Array.from(carsById.values());
  }, [cars, locallySavedCarsById]);

  useEffect(() => {
    if (!Array.isArray(cars) || cars.length === 0) return;

    const serverCarIds = new Set(
      cars.map((car: any) => getRecordId(car)).filter(Boolean),
    );

    setLocallySavedCarsById((current) => {
      const entries = Object.entries(current).filter(
        ([carId]) => !serverCarIds.has(carId),
      );

      if (entries.length === Object.keys(current).length) {
        return current;
      }

      return Object.fromEntries(entries);
    });
  }, [cars]);

  const canManageRecord = React.useCallback(
    (record: any) => {
      if (accessLoading || !currentUserUid) return false;
      if (isSuperAdmin) return true;

      return getRecordUserId(record) === currentUserUid;
    },
    [accessLoading, currentUserUid, isSuperAdmin],
  );

  const visibleCars = React.useMemo(() => {
    if (accessLoading || !currentUserUid) return [];
    if (isSuperAdmin) return allCars;

    return allCars.filter(
      (car: any) => getRecordUserId(car) === currentUserUid,
    );
  }, [accessLoading, allCars, currentUserUid, isSuperAdmin]);

  const visibleTrips = React.useMemo(() => {
    if (accessLoading || !currentUserUid) return [];
    if (isSuperAdmin) return trips;

    return trips.filter(
      (trip: any) => getRecordUserId(trip) === currentUserUid,
    );
  }, [accessLoading, currentUserUid, isSuperAdmin, trips]);

  const driverOptions = React.useMemo(() => {
    const options = new Map<string, DriverOption>();

    if (accountOwnerDriver?.id) {
      options.set(accountOwnerDriver.id, accountOwnerDriver);
    }

    members.forEach((member: any) => {
      const option = toDriverOption(member, "member");

      if (option && !options.has(option.id)) {
        options.set(option.id, option);
      }
    });

    Object.values(directoryDriversById).forEach((option) => {
      if (option?.id) {
        options.set(option.id, option);
      }
    });

    return Array.from(options.values()).sort((first, second) =>
      first.name.localeCompare(second.name),
    );
  }, [accountOwnerDriver, directoryDriversById, members]);

  const getSelectedDriverName = React.useCallback(
    (driverId: string) => {
      const normalizedDriverId = String(driverId || "").trim();

      if (!normalizedDriverId) return "";

      return (
        driverOptions.find((driver) => driver.id === normalizedDriverId)
          ?.name ||
        driverNamesById[normalizedDriverId] ||
        "Selected Driver"
      );
    },
    [driverNamesById, driverOptions],
  );

  const renderSelectedDriverCard = (driverId: string, onClear: () => void) => {
    const normalizedDriverId = String(driverId || "").trim();

    if (!normalizedDriverId) return null;

    const selectedDriver = driverOptions.find(
      (driver) => driver.id === normalizedDriverId,
    );

    const selectedDriverName =
      selectedDriver?.name || getSelectedDriverName(normalizedDriverId);
    const selectedDriverEmail = selectedDriver?.email || "";
    const selectedDriverPhoto = selectedDriver?.photoURL;

    return (
      <Box
        mt={4}
        p={4}
        border="1px solid"
        borderColor={theme.border}
        borderRadius={cardsBorderRadii}
        bg={theme.surfaceAlt}
      >
        <Flex
          gap={3}
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
        >
          <HStack flex="1" minW={0}>
            <Avatar
              size="md"
              src={selectedDriverPhoto}
              name={selectedDriverName}
            />

            <Box minW={0}>
              <Text fontWeight="800" color={theme.text} noOfLines={1}>
                {selectedDriverName}
              </Text>
              <Text fontSize="sm" color={theme.textMuted} noOfLines={1}>
                {selectedDriverEmail || normalizedDriverId}
              </Text>
              <Text fontSize="xs" color={theme.textSubtle} noOfLines={1}>
                {normalizedDriverId}
              </Text>
            </Box>
          </HStack>

          <Button
            size="sm"
            variant="ghost"
            color={theme.text}
            _hover={{ bg: theme.hover }}
            onClick={onClear}
          >
            Change Driver
          </Button>
        </Flex>
      </Box>
    );
  };

  const renderDriverPicker = ({
    label,
    selectedDriverId: driverId,
    onDriverChange,
    onDirectorySelect,
    onClear,
  }: {
    label: string;
    selectedDriverId: string;
    onDriverChange: (driverId: string) => void;
    onDirectorySelect: (user: UserMember) => void | Promise<void>;
    onClear: () => void;
  }) => (
    <FormControl>
      <FormLabel mb={1} fontWeight="800" color={theme.text}>
        {label}
      </FormLabel>

      <Text fontSize="sm" color={theme.textMuted} mb={3}>
        Choose the account owner or an existing member, or search any registered
        PickDengu user below.
      </Text>

      <Select
        value={driverId}
        onChange={(event) => onDriverChange(event.target.value)}
        bg={theme.inputBg}
        color={driverId ? theme.text : theme.inputPlaceholder}
        borderColor={theme.inputBorder}
        _hover={{ borderColor: theme.inputBorderFocus }}
        _focus={{
          borderColor: theme.inputBorderFocus,
          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
        }}
      >
        <option value="">Select an account driver</option>
        {driverOptions.map((driver) => (
          <option key={driver.id} value={driver.id}>
            {driver.name}
            {driver.email ? ` - ${driver.email}` : ""}
            {driver.source === "owner" ? " - Account owner" : ""}
          </option>
        ))}
      </Select>

      {driverOptions.length === 0 && (
        <Text mt={2} fontSize="sm" color={theme.textMuted}>
          There are no account drivers yet. Search for a registered user below.
        </Text>
      )}

      <Box
        mt={4}
        p={4}
        border="1px solid"
        borderColor={theme.border}
        borderRadius={cardsBorderRadii}
        bg={theme.surfaceAlt}
      >
        <Text fontWeight="800" color={theme.text} mb={1}>
          Search registered users
        </Text>
        <Text fontSize="sm" color={theme.textMuted} mb={3}>
          Type at least two characters of the user&apos;s name, email address,
          or Firebase UID, then select the result.
        </Text>

        <UserSearchDropdown onSelect={onDirectorySelect} />
      </Box>

      {renderSelectedDriverCard(driverId, onClear)}
    </FormControl>
  );

  const carTypes = [
    "Sedan",
    "SUV",
    "MiniBus",
    "Bus",
    "Truck",
    "Motorbike",
    "Van",
    "Hatchback",
    "Pickup",
  ];

  const carCategories = [
    "SUV",
    "Sedan",
    "Hatchback",
    "Pickup",
    "Van",
    "Minibus",
    "Bus",
    "Luxury",
    "4x4",
    "Other",
  ];

  const parseDate = (dateStr: string) => {
    if (!dateStr) return null;

    const d = new Date(dateStr);

    return isNaN(d.getTime()) ? null : d;
  };

  const handleDateChange = (date: Date | null) => {
    setTripForm((prev: any) => ({
      ...prev,
      departDate: date ? toLocalDateValue(date) : "",
    }));
  };

  const handleArriveDateChange = (date: Date | null) => {
    setTripForm((prev: any) => ({
      ...prev,
      arriveDate: date ? toLocalDateValue(date) : "",
    }));
  };

  const [carForm, setCarForm] = useState<any>({
    userId: "",
    driverId: "",
    name: "",
    type: "",
    category: "SUV",
    seats: "",
    numberPlate: "",
  });

  const [tripLocationKey, setTripLocationKey] = useState(0);
  const [fromAddress, setFromAddress] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [fromCoords, setFromCoords] = useState<LocationCoords | null>(null);
  const [toCoords, setToCoords] = useState<LocationCoords | null>(null);

  const [tripForm, setTripForm] = useState<any>({
    _id: "",
    userId: "",
    carId: "",
    driverId: "",
    from: "",
    to: "",
    fromLat: "",
    fromLng: "",
    toLat: "",
    toLng: "",
    departDate: "",
    arriveDate: "",
    seatsAvailable: "",
    priceMk: "",
    departTime: "",
    arriveTime: "",
    carType: "",
    recurrenceEnabled: false,
    recurrenceFrequency: "none",
    recurrenceInterval: "1",
    recurrenceGenerateDaysAhead: "90",
    recurrenceEndsOn: "",
  });

  const isEditingCar = !!carForm._id;
  const isEditingTrip = !!tripForm._id;

  useEffect(() => {
    dispatch(fetchHikeCars());
    dispatch(fetchAllTrips());
  }, [dispatch]);

  const loadMembersForOwner = React.useCallback(
    async (ownerUid: string) => {
      const normalizedOwnerUid = String(ownerUid || "").trim();

      if (!normalizedOwnerUid) {
        setMembers([]);
        setAccountOwnerDriver(null);
        return;
      }

      if (!isSuperAdmin && normalizedOwnerUid !== currentUserUid) {
        setMembers([]);
        setAccountOwnerDriver(null);
        return;
      }

      try {
        const ownerSnap = await getDoc(doc(db, "users", normalizedOwnerUid));

        if (!ownerSnap.exists()) {
          setMembers([]);
          setAccountOwnerDriver(null);
          return;
        }

        const ownerData = ownerSnap.data();
        setAccountOwnerDriver(
          toDriverOption(
            {
              ...ownerData,
              id: normalizedOwnerUid,
            },
            "owner",
          ),
        );
        setMembers(Array.isArray(ownerData.members) ? ownerData.members : []);
      } catch (error) {
        console.error(
          `Failed to load members for ${normalizedOwnerUid}:`,
          error,
        );
        setMembers([]);
        setAccountOwnerDriver(null);
      }
    },
    [currentUserUid, isSuperAdmin],
  );

  useEffect(() => {
    if (!user?.uid) {
      setMembers([]);
      setAccountOwnerDriver(null);
      setCurrentUserRole("");
      setAccessLoading(false);
      return;
    }

    let cancelled = false;
    setAccessLoading(true);

    const fetchCurrentUserAccess = async () => {
      try {
        const docSnap = await getDoc(doc(db, "users", user.uid));

        if (cancelled) return;

        if (!docSnap.exists()) {
          setCurrentUserRole("");
          setMembers([]);
          setAccountOwnerDriver(
            toDriverOption(
              {
                id: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL,
              },
              "owner",
            ),
          );
          return;
        }

        const data = docSnap.data();

        // Only the stored `role` field may grant superadmin access.
        setCurrentUserRole(String(data.role || ""));
        setAccountOwnerDriver(
          toDriverOption(
            {
              ...data,
              id: user.uid,
              displayName: data.displayName || user.displayName,
              email: data.email || user.email,
              photoURL: data.photoURL || user.photoURL,
            },
            "owner",
          ),
        );
        setMembers(Array.isArray(data.members) ? data.members : []);
      } catch (error) {
        console.error("Failed to load current user access:", error);

        if (!cancelled) {
          // Fail closed: no role means no cross-account access.
          setCurrentUserRole("");
          setMembers([]);
          setAccountOwnerDriver(null);
        }
      } finally {
        if (!cancelled) {
          setAccessLoading(false);
        }
      }
    };

    fetchCurrentUserAccess();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    const driverIds = Array.from(
      new Set(
        visibleTrips
          .map((trip: any) => String(trip?.driverId || "").trim())
          .filter(Boolean),
      ),
    );

    if (driverIds.length === 0) {
      setDriverNamesById({});
      return;
    }

    let cancelled = false;

    const fetchDriverNames = async () => {
      const entries = await Promise.all(
        driverIds.map(async (driverId) => {
          const existingDriver = driverOptions.find(
            (driver) => driver.id === driverId,
          );

          if (existingDriver?.name) {
            return [driverId, existingDriver.name] as const;
          }

          try {
            const driverSnap = await getDoc(doc(db, "users", driverId));

            if (driverSnap.exists()) {
              const driverData = driverSnap.data();
              const driverName = String(
                driverData.displayName ||
                  driverData.name ||
                  driverData.businessName ||
                  driverData.email ||
                  "Unknown driver",
              ).trim();

              return [driverId, driverName || "Unknown driver"] as const;
            }
          } catch (error) {
            console.error(`Failed to resolve driver ${driverId}:`, error);
          }

          return [driverId, "Unknown driver"] as const;
        }),
      );

      if (!cancelled) {
        setDriverNamesById(Object.fromEntries(entries));
      }
    };

    fetchDriverNames();

    return () => {
      cancelled = true;
    };
  }, [driverOptions, visibleTrips]);

  const getDriverDisplayName = (trip: any) => {
    const driverId = String(trip?.driverId || "").trim();

    if (!driverId) return "Missing";

    return (
      driverOptions.find((driver) => driver.id === driverId)?.name ||
      driverNamesById[driverId] ||
      String(trip?.driverName || "").trim() ||
      "Loading driver..."
    );
  };

  const resetCarForm = () =>
    setCarForm({
      userId: currentUserUid,
      driverId: "",
      name: "",
      type: "",
      category: "SUV",
      seats: "",
      numberPlate: "",
    });

  const resetTripForm = () => {
    setTripForm({
      _id: "",
      userId: currentUserUid,
      carId: "",
      driverId: "",
      from: "",
      to: "",
      fromLat: "",
      fromLng: "",
      toLat: "",
      toLng: "",
      departDate: "",
      arriveDate: "",
      seatsAvailable: "",
      priceMk: "",
      departTime: "",
      arriveTime: "",
      carType: "",
      recurrenceEnabled: false,
      recurrenceFrequency: "none",
      recurrenceInterval: "1",
      recurrenceGenerateDaysAhead: "90",
      recurrenceEndsOn: "",
    });

    setFromAddress("");
    setToAddress("");
    setFromCoords(null);
    setToCoords(null);
    setTripLocationKey((prev) => prev + 1);
  };

  const handleCarChange = (e: any) =>
    setCarForm({ ...carForm, [e.target.name]: e.target.value });

  const handleTripChange = (e: any) =>
    setTripForm({ ...tripForm, [e.target.name]: e.target.value });

  const handleFromAddressChange = React.useCallback((value: string) => {
    const nextValue = String(value || "");

    setFromAddress(nextValue);

    setTripForm((prev: any) => ({
      ...prev,
      from: nextValue,
    }));
  }, []);

  const handleToAddressChange = React.useCallback((value: string) => {
    const nextValue = String(value || "");

    setToAddress(nextValue);

    setTripForm((prev: any) => ({
      ...prev,
      to: nextValue,
    }));
  }, []);

  const handleFromLocationSelected = React.useCallback(
    (coords: LocationCoords | null) => {
      setFromCoords(coords);

      setTripForm((prev: any) => ({
        ...prev,
        fromLat: coords?.lat ?? "",
        fromLng: coords?.lng ?? "",
      }));
    },
    [],
  );

  const handleToLocationSelected = React.useCallback(
    (coords: LocationCoords | null) => {
      setToCoords(coords);

      setTripForm((prev: any) => ({
        ...prev,
        toLat: coords?.lat ?? "",
        toLng: coords?.lng ?? "",
      }));
    },
    [],
  );

  const rememberDirectoryDriver = React.useCallback((selectedUser: any) => {
    const option = toDriverOption(selectedUser, "directory");

    if (!option) return null;

    setDirectoryDriversById((current) => ({
      ...current,
      [option.id]: option,
    }));

    return option;
  }, []);

  const handleTripDriverSearchSelect = async (selectedUser: any) => {
    const option = rememberDirectoryDriver(selectedUser);

    if (!option) {
      toast({
        title: "Driver could not be selected",
        status: "error",
      });
      return;
    }

    setTripForm((current: any) => ({
      ...current,
      driverId: option.id,
    }));

    toast({
      title: "Driver selected",
      description: `${option.name} will drive this trip.`,
      status: "success",
      duration: 2200,
    });
  };

  const handleCarDriverSearchSelect = async (selectedUser: any) => {
    const option = rememberDirectoryDriver(selectedUser);

    if (!option) {
      toast({
        title: "Driver could not be selected",
        status: "error",
      });
      return;
    }

    setSelectedDriverId(option.id);
    setCarForm((current: any) => ({
      ...current,
      driverId: option.id,
    }));

    toast({
      title: "Driver selected",
      description: `${option.name} will be the default driver for this car.`,
      status: "success",
      duration: 2200,
    });
  };

  const handleTripCarSelect = (car: any) => {
    const carId = getRecordId(car);

    if (!carId) {
      toast({
        title: "Car could not be selected",
        description: "The selected car does not have a valid database ID.",
        status: "error",
      });
      return;
    }

    setTripForm((prev: any) => ({
      ...prev,
      carId,
      driverId: prev.driverId || car.driverId || "",
      carType: prev.carType || car.type || "",
      seatsAvailable: prev.seatsAvailable || car.seats || "",
    }));
  };

  const handleOpenCreateTrip = () => {
    resetTripForm();
    void loadMembersForOwner(currentUserUid);
    openTripModal();
  };

  const handleOpenCreateCar = () => {
    resetCarForm();
    setSelectedDriverId("");
    void loadMembersForOwner(currentUserUid);
    openCarModal();
  };

  const handleGenerateSchedulesNow = async () => {
    if (!isSuperAdmin) {
      toast({
        title: "Access denied",
        description: "Only superadmins can generate schedules across accounts.",
        status: "error",
      });
      return;
    }

    try {
      const result = await dispatch(generateRecurringTrips()).unwrap();

      toast({
        title: "Schedules generated",
        description: `Created ${result?.result?.created || 0} trips. Skipped ${result?.result?.skippedExisting || 0} existing trips.`,
        status: "success",
      });

      dispatch(fetchAllTrips());
    } catch {
      toast({
        title: "Failed to generate schedules",
        status: "error",
      });
    }
  };

  const saveCar = async () => {
    try {
      if (!currentUserUid) {
        toast({
          title: "You must be logged in to save a car",
          status: "error",
        });
        return;
      }

      const existingCar = isEditingCar
        ? allCars.find((car: any) => getRecordId(car) === carForm._id)
        : null;

      if (isEditingCar && (!existingCar || !canManageRecord(existingCar))) {
        toast({
          title: "Access denied",
          description: "You can only update cars that belong to your account.",
          status: "error",
        });
        return;
      }

      const ownerUid = isSuperAdmin
        ? getRecordUserId(existingCar) ||
          String(carForm.userId || "").trim() ||
          currentUserUid
        : currentUserUid;

      const resolvedCarDriverId = String(
        selectedDriverId || carForm.driverId || "",
      ).trim();

      if (!resolvedCarDriverId) {
        toast({
          title: "Please select a driver",
          description:
            "Choose the account owner, an existing member, or search registered users.",
          status: "error",
        });
        return;
      }

      const selectedDriver = driverOptions.find(
        (driver) => driver.id === resolvedCarDriverId,
      );

      // Never send Mongo-managed fields in create or update payloads.
      // In particular, `_id: ""` causes Mongoose to throw a BSON ObjectId cast error.
      const {
        _id: ignoredCarId,
        __v: ignoredVersion,
        createdAt: ignoredCreatedAt,
        updatedAt: ignoredUpdatedAt,
        ...editableCarFields
      } = carForm;

      const carPayload = {
        ...editableCarFields,
        userId: ownerUid,
        driverId: resolvedCarDriverId,
        driverName:
          selectedDriver?.name || getSelectedDriverName(resolvedCarDriverId),
        driverEmail: selectedDriver?.email || "",
        category: String(carForm.category || "Other").trim() || "Other",
        seats: parseInt(carForm.seats, 10) || 0,
      };

      let savedCarResponse: any;

      if (isEditingCar) {
        const carId = String(carForm._id || "").trim();

        if (!carId) {
          toast({
            title: "Car ID is missing",
            description:
              "Close the drawer and open the car again before editing.",
            status: "error",
          });
          return;
        }

        savedCarResponse = await dispatch(
          updateHikeCar({ id: carId, updates: carPayload } as any),
        ).unwrap();

        const returnedCar = extractSavedHikeCar(savedCarResponse);
        const locallyUpdatedCar = returnedCar || {
          ...(existingCar || {}),
          ...carPayload,
          _id: carId,
        };

        setLocallySavedCarsById((current) => ({
          ...current,
          [carId]: locallyUpdatedCar,
        }));

        toast({ title: "Car updated", status: "success" });
      } else {
        savedCarResponse = await dispatch(
          addHikeCar(carPayload as any),
        ).unwrap();

        const returnedCar = extractSavedHikeCar(savedCarResponse);

        if (returnedCar) {
          setLocallySavedCarsById((current) => ({
            ...current,
            [returnedCar._id]: {
              ...carPayload,
              ...returnedCar,
              _id: returnedCar._id,
            },
          }));
        }

        toast({ title: "Car added", status: "success" });
      }

      resetCarForm();
      closeCarModal();

      try {
        await dispatch(fetchHikeCars()).unwrap();
      } catch (refreshError) {
        console.error(
          "Car saved, but the car list refresh failed:",
          refreshError,
        );
        toast({
          title: "Car saved",
          description:
            "The car was saved and added locally, but the server list could not be refreshed.",
          status: "warning",
          duration: 3500,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error saving car:", error);
      toast({
        title: "Error saving car",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
      });
    }
  };

  const saveTrip = async () => {
    try {
      if (!user?.uid) {
        toast({
          title: "You must be logged in to save a trip",
          status: "error",
        });
        return;
      }

      const selectedCar = visibleCars.find(
        (car: any) => car._id === tripForm.carId,
      );
      const resolvedDriverId = tripForm.driverId || selectedCar?.driverId || "";

      if (!resolvedDriverId) {
        toast({
          title: "Please select a trip driver",
          status: "error",
        });
        return;
      }

      const resolvedFromAddress = String(
        fromAddress || tripForm.from || "",
      ).trim();
      const resolvedToAddress = String(toAddress || tripForm.to || "").trim();

      const resolvedFromCoords =
        fromCoords || buildCoords(tripForm.fromLat, tripForm.fromLng);

      const resolvedToCoords =
        toCoords || buildCoords(tripForm.toLat, tripForm.toLng);

      if (!resolvedFromAddress) {
        toast({
          title: "Please select a from address",
          status: "error",
        });
        return;
      }

      if (!resolvedToAddress) {
        toast({
          title: "Please select a to address",
          status: "error",
        });
        return;
      }

      if (!resolvedFromCoords) {
        toast({
          title: "Please select a valid Malawi pickup address",
          description:
            "Start typing and choose a location from the Google Maps suggestions.",
          status: "error",
          duration: 3500,
          isClosable: true,
        });
        return;
      }

      if (!resolvedToCoords) {
        toast({
          title: "Please select a valid Malawi destination address",
          description:
            "Start typing and choose a location from the Google Maps suggestions.",
          status: "error",
          duration: 3500,
          isClosable: true,
        });
        return;
      }

      const existingTrip = isEditingTrip
        ? trips.find((trip: any) => trip._id === tripForm._id)
        : null;

      if (isEditingTrip && (!existingTrip || !canManageRecord(existingTrip))) {
        toast({
          title: "Access denied",
          description: "You can only update trips that belong to your account.",
          status: "error",
        });
        return;
      }

      const ownerUid = isSuperAdmin
        ? getRecordUserId(existingTrip) ||
          String(tripForm.userId || "").trim() ||
          user.uid
        : user.uid;

      const recurrenceEnabled = Boolean(tripForm.recurrenceEnabled);
      const recurrenceFrequency = normalizeTripRecurrenceFrequency(
        tripForm.recurrenceFrequency,
      );

      const selectedDriver = driverOptions.find(
        (driver) => driver.id === resolvedDriverId,
      );

      const payload: Partial<HikeTrip> & {
        driverName?: string;
        driverEmail?: string;
      } = {
        userId: ownerUid,
        carId: tripForm.carId,
        driverId: resolvedDriverId,
        driverName:
          selectedDriver?.name || getSelectedDriverName(resolvedDriverId),
        driverEmail: selectedDriver?.email || "",
        from: {
          address: resolvedFromAddress,
          lat: resolvedFromCoords.lat,
          lng: resolvedFromCoords.lng,
        },
        to: {
          address: resolvedToAddress,
          lat: resolvedToCoords.lat,
          lng: resolvedToCoords.lng,
        },
        departDate: new Date(tripForm.departDate).toISOString(),
        arriveDate: new Date(tripForm.arriveDate).toISOString(),
        date: new Date(tripForm.departDate).toISOString(),
        seatsAvailable: parseInt(tripForm.seatsAvailable) || 0,
        priceMk: parseInt(tripForm.priceMk) || 0,
        departTime: tripForm.departTime,
        arriveTime: tripForm.arriveTime,
        carType: tripForm.carType,

        isTemplate: recurrenceEnabled,
        isGeneratedOccurrence: false,

        recurrence: {
          enabled: recurrenceEnabled,
          frequency: recurrenceEnabled ? recurrenceFrequency : "none",
          interval: parseInt(tripForm.recurrenceInterval) || 1,
          generateDaysAhead:
            parseInt(tripForm.recurrenceGenerateDaysAhead) || 90,
          startsOn: tripForm.departDate
            ? new Date(tripForm.departDate).toISOString()
            : null,
          endsOn: tripForm.recurrenceEndsOn
            ? new Date(tripForm.recurrenceEndsOn).toISOString()
            : null,
          lastGeneratedUntil: null,
        },
      };

      if (isEditingTrip) {
        await dispatch(
          updateTrip({ id: tripForm._id, updates: payload }),
        ).unwrap();
        toast({ title: "Trip updated", status: "success" });
      } else {
        await dispatch(addTrip(payload)).unwrap();
        toast({ title: "Trip created", status: "success" });
      }

      resetTripForm();
      closeTripModal();
      dispatch(fetchAllTrips());
    } catch {
      toast({ title: "Error saving trip", status: "error" });
    }
  };

  const handleEditTrip = (trip: any) => {
    if (!canManageRecord(trip)) {
      toast({
        title: "Access denied",
        description: "You can only edit trips that belong to your account.",
        status: "error",
      });
      return;
    }

    const tripOwnerUid = getRecordUserId(trip) || currentUserUid;
    void loadMembersForOwner(tripOwnerUid);

    const nextFromAddress = getLocationAddress(trip.from);
    const nextToAddress = getLocationAddress(trip.to);

    const nextFromCoords = buildCoords(
      getLocationLat(trip.from),
      getLocationLng(trip.from),
    );

    const nextToCoords = buildCoords(
      getLocationLat(trip.to),
      getLocationLng(trip.to),
    );

    setFromAddress(nextFromAddress);
    setToAddress(nextToAddress);
    setFromCoords(nextFromCoords);
    setToCoords(nextToCoords);
    setTripLocationKey((prev) => prev + 1);

    setTripForm({
      ...trip,
      userId: tripOwnerUid,
      from: nextFromAddress,
      to: nextToAddress,
      fromLat: nextFromCoords?.lat ?? "",
      fromLng: nextFromCoords?.lng ?? "",
      toLat: nextToCoords?.lat ?? "",
      toLng: nextToCoords?.lng ?? "",
      departDate: toInputDateValue(trip.departDate || trip.date),
      arriveDate: toInputDateValue(trip.arriveDate),
      seatsAvailable: String(trip.seatsAvailable ?? ""),
      priceMk: String(trip.priceMk ?? ""),
      departTime: trip.departTime || "",
      arriveTime: trip.arriveTime || "",
      carType: trip.carType || "",
      driverId: trip.driverId || "",
      carId: trip.carId || "",
      recurrenceEnabled: Boolean(trip.recurrence?.enabled),
      recurrenceFrequency: trip.recurrence?.frequency || "none",
      recurrenceInterval: String(trip.recurrence?.interval || 1),
      recurrenceGenerateDaysAhead: String(
        trip.recurrence?.generateDaysAhead || 90,
      ),
      recurrenceEndsOn: toInputDateValue(trip.recurrence?.endsOn),
    });

    openTripModal();
  };

  const handleDeleteTrip = (id: string) => {
    const trip = trips.find((item: any) => item._id === id);

    if (!trip || !canManageRecord(trip)) {
      toast({
        title: "Access denied",
        description: "You can only delete trips that belong to your account.",
        status: "error",
      });
      return;
    }

    dispatch(deleteTrip(id));
  };

  const handleEditCar = (car: any) => {
    if (!canManageRecord(car)) {
      toast({
        title: "Access denied",
        description: "You can only edit cars that belong to your account.",
        status: "error",
      });
      return;
    }

    const carOwnerUid = getRecordUserId(car) || currentUserUid;
    void loadMembersForOwner(carOwnerUid);
    setSelectedDriverId(car.driverId || "");
    setCarForm({
      ...car,
      userId: carOwnerUid,
      category: String(car.category || "Other").trim() || "Other",
    });
    openCarModal();
  };

  const handleDeleteCar = async (id: string) => {
    const car = allCars.find((item: any) => getRecordId(item) === id);

    if (!car || !canManageRecord(car)) {
      toast({
        title: "Access denied",
        description: "You can only delete cars that belong to your account.",
        status: "error",
      });
      return;
    }

    try {
      await dispatch(deleteHikeCar(id)).unwrap();

      setLocallySavedCarsById((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });

      toast({ title: "Car deleted", status: "success" });
    } catch (error) {
      toast({
        title: "Failed to delete car",
        description:
          typeof error === "string"
            ? error
            : "The car could not be deleted. Please try again.",
        status: "error",
      });
    }
  };

  const inputStyles = {
    bg: theme.inputBg,
    color: theme.text,
    border: "1px solid",
    borderColor: theme.inputBorder,
    _placeholder: { fontWeight: "500", color: theme.inputPlaceholder },
    _hover: { borderColor: theme.inputBorderFocus },
    _focus: {
      borderColor: theme.inputBorderFocus,
      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
    },
  };

  const menuListStyles = {
    maxH: "220px",
    overflowY: "auto" as const,
    border: "1px solid",
    borderColor: theme.border,
    bg: theme.surface,
    color: theme.text,
    boxShadow: theme.shadowSm,
    zIndex: 10000,
  };

  const primaryBtnStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients.primaryButton,
    _hover: { bgImage: theme.gradients.primaryButtonHover },
    boxShadow: theme.shadowSm,
    borderRadius: "6px",
  };

  const outlineBtnStyles = {
    borderColor: theme.borderStrong,
    color: theme.text,
    _hover: { bg: theme.hover },
  };

  const drawerContentStyles = {
    bg: theme.surface,
    color: theme.text,
    maxW: { base: "100vw", md: "760px" },
    w: { base: "100vw", md: "760px" },
    zIndex: 4000,
  };

  const drawerBodyStyles = {
    overflowY: "auto" as const,
    overflowX: "visible" as const,
    sx: {
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
    },
  };

  const renderTripForm = () => (
    <VStack spacing={3} align="stretch">
      <FormControl>
        <Menu strategy="fixed">
          <MenuButton
            border="1px solid"
            borderColor={theme.inputBorder}
            textAlign="left"
            borderWidth="1px"
            p={4}
            color={tripForm.carId ? theme.text : theme.inputPlaceholder}
            borderRadius={cardsBorderRadii}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
            bg={theme.inputBg}
            as={Button}
            rightIcon={<ChevronDownIcon />}
            width="100%"
          >
            {tripForm.carId
              ? allCars.find((c: any) => getRecordId(c) === tripForm.carId)
                  ?.name || "Selected Car"
              : "Select Car"}
          </MenuButton>
          <MenuList {...menuListStyles}>
            {visibleCars.map((car: any) => (
              <MenuItem
                key={getRecordId(car)}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
                _focus={{ bg: theme.hover }}
                onClick={() => handleTripCarSelect(car)}
              >
                {car.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </FormControl>

      {renderDriverPicker({
        label: "Select Trip Driver",
        selectedDriverId: tripForm.driverId,
        onDriverChange: (driverId) =>
          setTripForm((current: any) => ({
            ...current,
            driverId,
          })),
        onDirectorySelect: handleTripDriverSearchSelect,
        onClear: () =>
          setTripForm((current: any) => ({
            ...current,
            driverId: "",
          })),
      })}

      <Box w="100%" position="relative" zIndex={5}>
        <StablePlaceAutocomplete
          key={`trip-from-${tripLocationKey}`}
          resetKey={tripLocationKey}
          placeholder="From Address"
          initialValue={fromAddress}
          inputStyles={inputStyles}
          onAddressChange={handleFromAddressChange}
          onLocationSelected={handleFromLocationSelected}
          onInvalidPlace={() =>
            toast({
              title: "Please select a Malawi address",
              description: "Trip pickup locations must be inside Malawi.",
              status: "warning",
              duration: 3000,
              isClosable: true,
            })
          }
        />
      </Box>

      <Box w="100%" position="relative" zIndex={4}>
        <StablePlaceAutocomplete
          key={`trip-to-${tripLocationKey}`}
          resetKey={tripLocationKey}
          placeholder="To Address"
          initialValue={toAddress}
          inputStyles={inputStyles}
          onAddressChange={handleToAddressChange}
          onLocationSelected={handleToLocationSelected}
          onInvalidPlace={() =>
            toast({
              title: "Please select a Malawi address",
              description: "Trip destination locations must be inside Malawi.",
              status: "warning",
              duration: 3000,
              isClosable: true,
            })
          }
        />
      </Box>

      <VStack w="100%" spacing={3} align="stretch">
        <Box w="100%">
          <CustomDatePicker
            selected={parseDate(tripForm.departDate)}
            onChange={handleDateChange}
            placeholder="Departure Date"
            width="100%"
          />
        </Box>

        <Box w="100%">
          <CustomDatePicker
            selected={parseDate(tripForm.arriveDate)}
            onChange={handleArriveDateChange}
            placeholder="Arrival Date"
            width="100%"
          />
        </Box>
      </VStack>

      <Input
        {...inputStyles}
        name="seatsAvailable"
        placeholder="Seats Available"
        value={tripForm.seatsAvailable}
        onChange={handleTripChange}
      />

      <Input
        {...inputStyles}
        name="priceMk"
        placeholder="Price MK"
        value={tripForm.priceMk}
        onChange={handleTripChange}
      />

      <FormControl>
        <Menu strategy="fixed">
          <MenuButton
            border="1px solid"
            borderColor={theme.inputBorder}
            color={tripForm.departTime ? theme.text : theme.inputPlaceholder}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
            bgColor={theme.inputBg}
            textAlign="left"
            borderWidth="1px"
            p={4}
            borderRadius={cardsBorderRadii}
            as={Button}
            rightIcon={<ChevronDownIcon />}
            width="100%"
          >
            {tripForm.departTime
              ? timeOptions.find((t) => t.value === tripForm.departTime)?.label
              : "Select Depart Time"}
          </MenuButton>
          <MenuList {...menuListStyles}>
            {timeOptions.map((time) => (
              <MenuItem
                key={time.value}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
                _focus={{ bg: theme.hover }}
                onClick={() =>
                  setTripForm((prev: any) => ({
                    ...prev,
                    departTime: time.value,
                  }))
                }
              >
                {time.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </FormControl>

      <FormControl>
        <Menu strategy="fixed">
          <MenuButton
            textAlign="left"
            borderWidth="1px"
            p={4}
            border="1px solid"
            borderColor={theme.inputBorder}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
            borderRadius={cardsBorderRadii}
            bg={theme.inputBg}
            color={tripForm.arriveTime ? theme.text : theme.inputPlaceholder}
            as={Button}
            rightIcon={<ChevronDownIcon />}
            width="100%"
          >
            {tripForm.arriveTime
              ? timeOptions.find((t) => t.value === tripForm.arriveTime)?.label
              : "Select Arrival Time"}
          </MenuButton>
          <MenuList {...menuListStyles}>
            {timeOptions.map((time) => (
              <MenuItem
                key={time.value}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
                _focus={{ bg: theme.hover }}
                onClick={() =>
                  setTripForm((prev: any) => ({
                    ...prev,
                    arriveTime: time.value,
                  }))
                }
              >
                {time.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </FormControl>

      <FormControl>
        <Menu strategy="fixed">
          <MenuButton
            textAlign="left"
            borderWidth="1px"
            border="1px solid"
            borderColor={theme.inputBorder}
            p={4}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
            borderRadius={cardsBorderRadii}
            bg={theme.inputBg}
            as={Button}
            rightIcon={<ChevronDownIcon />}
            width="100%"
            color={tripForm.carType ? theme.text : theme.inputPlaceholder}
          >
            {tripForm.carType || "Select Car Type"}
          </MenuButton>
          <MenuList {...menuListStyles}>
            {carTypes.map((type) => (
              <MenuItem
                key={type}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
                _focus={{ bg: theme.hover }}
                onClick={() =>
                  setTripForm((prev: any) => ({
                    ...prev,
                    carType: type,
                  }))
                }
              >
                {type}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </FormControl>

      <Box
        w="100%"
        border="1px solid"
        borderColor={theme.border}
        borderRadius={cardsBorderRadii}
        p={3}
        bg={theme.surfaceAlt}
      >
        <VStack align="stretch" spacing={3}>
          <Flex justifyContent="space-between" alignItems="center" gap={3}>
            <Box>
              <Text fontWeight="bold" color={theme.text}>
                Repeat this trip
              </Text>
              <Text fontSize="sm" color={theme.textMuted}>
                Automatically generate future trips from this schedule.
              </Text>
            </Box>

            <Button
              size="sm"
              variant={tripForm.recurrenceEnabled ? "solid" : "outline"}
              {...(tripForm.recurrenceEnabled
                ? primaryBtnStyles
                : outlineBtnStyles)}
              onClick={() =>
                setTripForm((prev: any) => ({
                  ...prev,
                  recurrenceEnabled: !prev.recurrenceEnabled,
                  recurrenceFrequency: !prev.recurrenceEnabled
                    ? "weekly"
                    : "none",
                }))
              }
            >
              {tripForm.recurrenceEnabled ? "Enabled" : "Disabled"}
            </Button>
          </Flex>

          {tripForm.recurrenceEnabled && (
            <>
              <FormControl>
                <Menu strategy="fixed">
                  <MenuButton
                    textAlign="left"
                    borderWidth="1px"
                    border="1px solid"
                    borderColor={theme.inputBorder}
                    p={4}
                    _hover={{ borderColor: theme.inputBorderFocus }}
                    _focus={{
                      borderColor: theme.inputBorderFocus,
                      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                    }}
                    borderRadius={cardsBorderRadii}
                    bg={theme.inputBg}
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    width="100%"
                    color={theme.text}
                  >
                    {tripForm.recurrenceFrequency === "daily"
                      ? "Daily"
                      : tripForm.recurrenceFrequency === "monthly"
                        ? "Monthly"
                        : "Weekly"}
                  </MenuButton>
                  <MenuList {...menuListStyles}>
                    {[
                      { value: "daily", label: "Daily" },
                      { value: "weekly", label: "Weekly" },
                      { value: "monthly", label: "Monthly" },
                    ].map((option) => (
                      <MenuItem
                        key={option.value}
                        bg={theme.surface}
                        _hover={{ bg: theme.hover }}
                        _focus={{ bg: theme.hover }}
                        onClick={() =>
                          setTripForm((prev: any) => ({
                            ...prev,
                            recurrenceFrequency: option.value,
                          }))
                        }
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </FormControl>

              <Input
                {...inputStyles}
                name="recurrenceInterval"
                type="number"
                min={1}
                placeholder="Repeat every"
                value={tripForm.recurrenceInterval}
                onChange={handleTripChange}
              />

              <Input
                {...inputStyles}
                name="recurrenceGenerateDaysAhead"
                type="number"
                min={1}
                placeholder="Generate days ahead e.g. 90"
                value={tripForm.recurrenceGenerateDaysAhead}
                onChange={handleTripChange}
              />

              <Box w="100%">
                <CustomDatePicker
                  selected={parseDate(tripForm.recurrenceEndsOn)}
                  onChange={(date) =>
                    setTripForm((prev: any) => ({
                      ...prev,
                      recurrenceEndsOn: date ? toLocalDateValue(date) : "",
                    }))
                  }
                  placeholder="Optional schedule end date"
                  width="100%"
                />
              </Box>
            </>
          )}
        </VStack>
      </Box>
    </VStack>
  );

  const renderCarForm = () => (
    <VStack spacing={3} align="stretch">
      {renderDriverPicker({
        label: "Select Car Driver",
        selectedDriverId: carForm.driverId,
        onDriverChange: (driverId) => {
          setSelectedDriverId(driverId);
          setCarForm((current: any) => ({
            ...current,
            driverId,
          }));
        },
        onDirectorySelect: handleCarDriverSearchSelect,
        onClear: () => {
          setSelectedDriverId("");
          setCarForm((current: any) => ({
            ...current,
            driverId: "",
          }));
        },
      })}

      <Input
        {...inputStyles}
        placeholder="Car Name"
        name="name"
        value={carForm.name}
        onChange={handleCarChange}
      />

      <FormControl>
        <Menu strategy="fixed">
          <MenuButton
            border="1px solid"
            borderColor={theme.inputBorder}
            textAlign="left"
            borderWidth="1px"
            p={4}
            borderRadius={cardsBorderRadii}
            bg={theme.inputBg}
            as={Button}
            color={carForm.type ? theme.text : theme.inputPlaceholder}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
            rightIcon={<ChevronDownIcon />}
            width="100%"
          >
            {carForm.type || "Select Car Type"}
          </MenuButton>
          <MenuList {...menuListStyles}>
            {[
              "Small Car",
              "SUV",
              "Bus",
              "Minibus",
              "Van",
              "Truck",
              "Motor Bike",
            ].map((type) => (
              <MenuItem
                key={type}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
                _focus={{ bg: theme.hover }}
                onClick={() => setCarForm((prev: any) => ({ ...prev, type }))}
              >
                {type}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </FormControl>

      <FormControl>
        <FormLabel mb={1} fontWeight="700" color={theme.text}>
          Category
        </FormLabel>

        <Menu matchWidth strategy="fixed" placement="bottom-start" gutter={4}>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                width="100%"
                h="40px"
                minW={0}
                px={3}
                bg={theme.inputBg}
                color={carForm.category ? theme.text : theme.inputPlaceholder}
                border="1px solid"
                borderColor={
                  isOpen ? theme.inputBorderFocus : theme.inputBorder
                }
                borderRadius={cardsBorderRadii}
                fontWeight="500"
                textAlign="left"
                boxShadow={
                  isOpen ? `0 0 0 1px ${theme.inputBorderFocus}` : "none"
                }
                rightIcon={
                  <ChevronDownIcon
                    transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                    transition="transform 0.18s ease"
                  />
                }
                _hover={{
                  bg: theme.inputBg,
                  borderColor: theme.inputBorderFocus,
                }}
                _active={{ bg: theme.inputBg }}
                _focusVisible={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
                sx={{
                  "& > span:first-of-type": {
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                {carForm.category || "Select Category"}
              </MenuButton>

              <MenuList
                {...menuListStyles}
                width="100%"
                minW="100%"
                maxW="100%"
                p={1}
                borderRadius={cardsBorderRadii}
                overflowX="hidden"
              >
                {carCategories.map((category) => {
                  const selected = carForm.category === category;

                  return (
                    <MenuItem
                      key={category}
                      width="100%"
                      minW={0}
                      px={3}
                      py={2}
                      borderRadius="6px"
                      bg={selected ? theme.surfaceAlt : theme.surface}
                      color={selected ? theme.primary : theme.text}
                      fontWeight={selected ? "700" : "600"}
                      _hover={{ bg: theme.hover, color: theme.text }}
                      _focus={{ bg: theme.hover, color: theme.text }}
                      onClick={() =>
                        setCarForm((prev: any) => ({
                          ...prev,
                          category,
                        }))
                      }
                    >
                      {category}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </>
          )}
        </Menu>
      </FormControl>

      <Input
        {...inputStyles}
        placeholder="Number of Seats"
        name="seats"
        type="number"
        value={carForm.seats}
        onChange={handleCarChange}
      />

      <Input
        {...inputStyles}
        placeholder="Number Plate"
        name="numberPlate"
        value={carForm.numberPlate}
        onChange={handleCarChange}
      />
    </VStack>
  );

  if (loadError) {
    return (
      <Flex justify="center" align="center" minH="220px" w="100%">
        <Text color="red.500">Failed to load Google Maps autocomplete.</Text>
      </Flex>
    );
  }

  if (!isLoaded) {
    return (
      <Flex justify="center" align="center" minH="220px" w="100%" gap={3}>
        <Spinner size="lg" color={theme.primary} />
        <Text color={theme.textMuted}>Loading Google Maps autocomplete...</Text>
      </Flex>
    );
  }

  return (
    <>
      <Global
        styles={{
          ".pac-container": {
            zIndex: "99999 !important",
            borderRadius: cardsBorderRadii,
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.22)",
            border: `1px solid ${theme.border}`,
            overflow: "hidden",
          },
          ".pac-item": {
            padding: "10px 12px",
            cursor: "pointer",
            fontSize: "14px",
          },
          ".pac-item:hover": {
            backgroundColor: theme.hover,
          },
        }}
      />

      <Box
        bg={theme.surface}
        w="100%"
        p={{ base: 3, md: 4 }}
        border="1px solid"
        borderColor={theme.border}
        borderRadius={cardsBorderRadii}
        boxShadow={theme.shadowSm}
      >
        <Tabs variant="unstyled" defaultIndex={0} mb={8}>
          <TabList
            p={1}
            gap={2}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
            border="1px solid"
            borderColor={theme.border}
            flexWrap="wrap"
          >
            <Tab
              fontWeight="bold"
              color={theme.textMuted}
              borderRadius={cardsBorderRadii}
              px={4}
              py={2.5}
              _hover={{ bg: theme.hover, color: theme.text }}
              _selected={{
                color: theme.textOnPrimary,
                bgImage: theme.gradients.primaryButton,
                boxShadow: theme.shadowSm,
              }}
            >
              Manage Trips
            </Tab>
            <Tab
              fontWeight="bold"
              color={theme.textMuted}
              borderRadius={cardsBorderRadii}
              px={4}
              py={2.5}
              _hover={{ bg: theme.hover, color: theme.text }}
              _selected={{
                color: theme.textOnPrimary,
                bgImage: theme.gradients.primaryButton,
                boxShadow: theme.shadowSm,
              }}
            >
              Manage Cars
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={5}>
              <Flex
                justifyContent="space-between"
                alignItems={{ base: "flex-start", md: "center" }}
                gap={3}
                mb={4}
                direction={{ base: "column", md: "row" }}
              >
                <Box>
                  <Heading size="md" color={theme.text}>
                    Trips
                  </Heading>
                  <Text fontSize="sm" color={theme.textMuted} mt={1}>
                    Manage existing trips. Click edit to update a trip in the
                    side panel.
                  </Text>
                </Box>

                <Flex gap={2} flexWrap="wrap" w={{ base: "100%", md: "auto" }}>
                  {isSuperAdmin && (
                    <Button
                      variant="outline"
                      flex={{ base: 1, sm: "initial" }}
                      borderColor={theme.borderStrong}
                      color={theme.text}
                      _hover={{ bg: theme.hover }}
                      onClick={handleGenerateSchedulesNow}
                      isLoading={scheduleGenerationLoading}
                    >
                      Generate Schedules Now
                    </Button>
                  )}

                  <Button
                    {...primaryBtnStyles}
                    onClick={handleOpenCreateTrip}
                    flex={{ base: 1, sm: "initial" }}
                  >
                    Add Trip
                  </Button>
                </Flex>
              </Flex>

              <Box
                display={{ base: "none", md: "block" }}
                overflowX="auto"
                border="1px solid"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                bg={theme.surface}
                boxShadow={theme.shadowSm}
              >
                <Table size="sm" minW="680px">
                  <Thead bg={theme.surfaceAlt}>
                    <Tr>
                      <Th color={theme.textMuted}>From → To</Th>
                      <Th color={theme.textMuted}>Driver</Th>
                      <Th color={theme.textMuted}>Depart Date</Th>
                      <Th color={theme.textMuted}>Arrival Date</Th>
                      <Th color={theme.textMuted}>Seats</Th>
                      <Th color={theme.textMuted}>Schedule</Th>
                      <Th color={theme.textMuted}>Status</Th>
                      <Th textAlign="right" color={theme.textMuted}>
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleTrips.map((trip: any) => (
                      <Tr key={trip._id} _hover={{ bg: theme.hover }}>
                        <Td fontWeight="semibold" color={theme.text}>
                          {trip.from?.address} → {trip.to?.address}
                        </Td>
                        <Td color={theme.text}>{getDriverDisplayName(trip)}</Td>
                        <Td color={theme.text}>
                          {toInputDateValue(trip.departDate || trip.date)}
                        </Td>
                        <Td color={theme.text}>
                          {toInputDateValue(trip.arriveDate)}
                        </Td>
                        <Td color={theme.text}>{trip.seatsAvailable}</Td>
                        <Td color={theme.text}>
                          {trip.recurrence?.enabled ? (
                            <Badge colorScheme="purple">
                              {trip.recurrence.frequency}
                            </Badge>
                          ) : trip.isGeneratedOccurrence ? (
                            <Badge colorScheme="blue">Generated</Badge>
                          ) : (
                            <Badge colorScheme="gray">Single</Badge>
                          )}
                        </Td>
                        <Td color={theme.text}>
                          <Badge
                            colorScheme={
                              trip.status === "cancelled" ? "red" : "green"
                            }
                          >
                            {trip.status || "pending"}
                          </Badge>
                        </Td>
                        <Td>
                          <Flex justifyContent="flex-end" gap={2}>
                            <IconButton
                              size="xs"
                              icon={<EditIcon />}
                              onClick={() => handleEditTrip(trip)}
                              aria-label="Edit Trip"
                              variant="outline"
                              borderColor={theme.borderStrong}
                              color={theme.text}
                              _hover={{ bg: theme.hover }}
                            />
                            <IconButton
                              size="xs"
                              icon={<DeleteIcon />}
                              onClick={() => handleDeleteTrip(trip._id)}
                              aria-label="Delete Trip"
                              colorScheme="red"
                            />
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <VStack
                display={{ base: "flex", md: "none" }}
                spacing={3}
                align="stretch"
              >
                {visibleTrips.map((trip: any) => (
                  <Box
                    key={trip._id}
                    border="1px solid"
                    borderColor={theme.border}
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    p={4}
                  >
                    <Flex
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={3}
                      mb={3}
                    >
                      <Box minW={0}>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="wide"
                          color={theme.textMuted}
                          mb={1}
                        >
                          Route
                        </Text>
                        <Text
                          fontWeight="bold"
                          color={theme.text}
                          lineHeight="short"
                          wordBreak="break-word"
                        >
                          {trip.from?.address || "Missing pickup"} →{" "}
                          {trip.to?.address || "Missing destination"}
                        </Text>
                      </Box>

                      <Badge
                        flexShrink={0}
                        colorScheme={
                          trip.status === "cancelled" ? "red" : "green"
                        }
                      >
                        {trip.status || "pending"}
                      </Badge>
                    </Flex>

                    <VStack align="stretch" spacing={2.5}>
                      <Flex justifyContent="space-between" gap={3}>
                        <Text fontSize="sm" color={theme.textMuted}>
                          Driver
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={theme.text}
                          textAlign="right"
                        >
                          {getDriverDisplayName(trip)}
                        </Text>
                      </Flex>

                      <Flex justifyContent="space-between" gap={3}>
                        <Text fontSize="sm" color={theme.textMuted}>
                          Departure
                        </Text>
                        <Text
                          fontSize="sm"
                          color={theme.text}
                          textAlign="right"
                        >
                          {toInputDateValue(trip.departDate || trip.date) ||
                            "Missing"}
                        </Text>
                      </Flex>

                      <Flex justifyContent="space-between" gap={3}>
                        <Text fontSize="sm" color={theme.textMuted}>
                          Arrival
                        </Text>
                        <Text
                          fontSize="sm"
                          color={theme.text}
                          textAlign="right"
                        >
                          {toInputDateValue(trip.arriveDate) || "Missing"}
                        </Text>
                      </Flex>

                      <Flex justifyContent="space-between" gap={3}>
                        <Text fontSize="sm" color={theme.textMuted}>
                          Seats
                        </Text>
                        <Text fontSize="sm" color={theme.text}>
                          {trip.seatsAvailable ?? 0}
                        </Text>
                      </Flex>

                      <Flex justifyContent="space-between" gap={3}>
                        <Text fontSize="sm" color={theme.textMuted}>
                          Schedule
                        </Text>
                        {trip.recurrence?.enabled ? (
                          <Badge colorScheme="purple">
                            {trip.recurrence.frequency}
                          </Badge>
                        ) : trip.isGeneratedOccurrence ? (
                          <Badge colorScheme="blue">Generated</Badge>
                        ) : (
                          <Badge colorScheme="gray">Single</Badge>
                        )}
                      </Flex>
                    </VStack>

                    <Flex
                      justifyContent="flex-end"
                      gap={2}
                      mt={4}
                      pt={3}
                      borderTop="1px solid"
                      borderColor={theme.border}
                    >
                      <Button
                        size="sm"
                        leftIcon={<EditIcon />}
                        onClick={() => handleEditTrip(trip)}
                        variant="outline"
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                        flex={1}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<DeleteIcon />}
                        onClick={() => handleDeleteTrip(trip._id)}
                        colorScheme="red"
                        flex={1}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </TabPanel>

            <TabPanel px={0} pt={5}>
              <Flex
                justifyContent="space-between"
                alignItems={{ base: "flex-start", md: "center" }}
                gap={3}
                mb={4}
                direction={{ base: "column", md: "row" }}
              >
                <Box>
                  <Heading size="md" color={theme.text}>
                    Cars
                  </Heading>
                  <Text fontSize="sm" color={theme.textMuted} mt={1}>
                    Manage trip cars. Click edit to update a car in the side
                    panel.
                  </Text>
                </Box>

                <Button {...primaryBtnStyles} onClick={handleOpenCreateCar}>
                  Add Car
                </Button>
              </Flex>

              <Box
                overflowX="auto"
                mb={6}
                border="1px solid"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                bg={theme.surface}
                boxShadow={theme.shadowSm}
              >
                <Table size="sm" minW="700px">
                  <Thead bg={theme.surfaceAlt}>
                    <Tr>
                      <Th color={theme.textMuted}>Name</Th>
                      <Th color={theme.textMuted}>Type</Th>
                      <Th color={theme.textMuted}>Category</Th>
                      <Th color={theme.textMuted}>Plate</Th>
                      <Th textAlign="right" color={theme.textMuted}>
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleCars.map((car: any) => {
                      const carId = getRecordId(car);

                      return (
                        <Tr key={carId} _hover={{ bg: theme.hover }}>
                          <Td fontWeight="semibold" color={theme.text}>
                            {car.name}
                          </Td>
                          <Td color={theme.text}>{car.type}</Td>
                          <Td color={theme.text}>{car.category || "Other"}</Td>
                          <Td color={theme.text}>{car.numberPlate}</Td>
                          <Td textAlign="right">
                            <Flex gap={2} justifyContent="flex-end">
                              <IconButton
                                size="xs"
                                icon={<EditIcon />}
                                onClick={() => handleEditCar(car)}
                                aria-label="Edit Car"
                                variant="outline"
                                borderColor={theme.borderStrong}
                                color={theme.text}
                                _hover={{ bg: theme.hover }}
                              />
                              <IconButton
                                size="xs"
                                icon={<DeleteIcon />}
                                onClick={() => handleDeleteCar(carId)}
                                aria-label="Delete Car"
                                colorScheme="red"
                              />
                            </Flex>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Drawer
          isOpen={isTripModalOpen}
          placement="right"
          onClose={() => {
            closeTripModal();
            resetTripForm();
          }}
          size="xl"
          trapFocus={false}
          returnFocusOnClose={false}
          blockScrollOnMount={false}
        >
          <DrawerOverlay bg={theme.overlay || "rgba(15, 23, 42, 0.45)"} />
          <DrawerContent {...drawerContentStyles}>
            <DrawerCloseButton />
            <DrawerHeader
              borderBottom="1px solid"
              borderColor={theme.border}
              pr={12}
            >
              <Box>
                <Heading size="md" color={theme.text}>
                  {isEditingTrip ? "Edit Trip" : "Create Trip"}
                </Heading>
                <Text fontSize="sm" color={theme.textMuted} mt={1}>
                  Start typing in the address fields to search Google Maps
                  locations in Malawi.
                </Text>
              </Box>
            </DrawerHeader>

            <DrawerBody {...drawerBodyStyles}>{renderTripForm()}</DrawerBody>

            <DrawerFooter
              gap={3}
              borderTop="1px solid"
              borderColor={theme.border}
              bg={theme.surface}
            >
              <Button
                variant="outline"
                {...outlineBtnStyles}
                onClick={() => {
                  closeTripModal();
                  resetTripForm();
                }}
              >
                Cancel
              </Button>
              <Button {...primaryBtnStyles} onClick={saveTrip}>
                {isEditingTrip ? "Update" : "Create"} Trip
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <Drawer
          isOpen={isCarModalOpen}
          placement="right"
          onClose={() => {
            closeCarModal();
            resetCarForm();
            setSelectedDriverId("");
          }}
          size="md"
          trapFocus={false}
          returnFocusOnClose={false}
          blockScrollOnMount={false}
        >
          <DrawerOverlay bg={theme.overlay || "rgba(15, 23, 42, 0.45)"} />
          <DrawerContent
            bg={theme.surface}
            color={theme.text}
            maxW={{ base: "100vw", md: "520px" }}
            w={{ base: "100vw", md: "520px" }}
            zIndex={4000}
          >
            <DrawerCloseButton />
            <DrawerHeader
              borderBottom="1px solid"
              borderColor={theme.border}
              pr={12}
            >
              <Box>
                <Heading size="md" color={theme.text}>
                  {isEditingCar ? "Edit Car" : "Add Car"}
                </Heading>
                <Text fontSize="sm" color={theme.textMuted} mt={1}>
                  Add or update the car details used for hike and trip
                  schedules.
                </Text>
              </Box>
            </DrawerHeader>

            <DrawerBody {...drawerBodyStyles}>{renderCarForm()}</DrawerBody>

            <DrawerFooter
              gap={3}
              borderTop="1px solid"
              borderColor={theme.border}
              bg={theme.surface}
            >
              <Button
                variant="outline"
                {...outlineBtnStyles}
                onClick={() => {
                  closeCarModal();
                  resetCarForm();
                  setSelectedDriverId("");
                }}
              >
                Cancel
              </Button>
              <Button {...primaryBtnStyles} onClick={saveCar}>
                {isEditingCar ? "Update" : "Add"} Car
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </>
  );
};

export default HikeCarsManager;
